import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { SERVICE_TYPE_OPTIONS, WHATSAPP_URL } from "../../lib/site-data";
import api, { formatApiError } from "../../lib/api";

const initial = {
    full_name: "",
    phone: "",
    service_type: SERVICE_TYPE_OPTIONS[0],
    region: "",
    color_or_appearance: "",
    size: "",
    duration: "",
    previous_treatment: "Hayır",
    contact_preference: "WhatsApp",
    message: "",
    kvkk_consent: false,
};

export default function LeadForm({ defaultService }) {
    const [data, setData] = useState({
        ...initial,
        service_type: defaultService || initial.service_type,
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k, v) => setData((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!data.kvkk_consent) {
            toast.error("KVKK onayı zorunludur.");
            return;
        }
        if (data.full_name.trim().length < 2 || data.phone.trim().length < 6) {
            toast.error("Lütfen ad-soyad ve telefon bilgilerinizi eksiksiz girin.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/leads", data);
            setSuccess(true);
            toast.success("Talebiniz alındı. En kısa sürede dönüş yapacağız.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <section
                id="lead-form"
                className="relative py-24 md:py-28 border-b border-white/5"
                data-testid="lead-form-success"
            >
                <div className="max-w-[760px] mx-auto px-6">
                    <div className="bg-[#0E0E0E] border border-cyan-400/30 rounded-2xl p-10 text-center relative overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-400/15 blur-[80px] rounded-full pointer-events-none"></div>
                        <CheckCircle2 className="h-12 w-12 text-cyan-300 mx-auto mb-5" />
                        <h3 className="font-display text-3xl md:text-4xl text-white mb-3">
                            Talebiniz alındı
                        </h3>
                        <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
                            Daha hızlı bir ön değerlendirme için lütfen dövme/iz fotoğrafını
                            WhatsApp üzerinden de bize iletin. En kısa sürede dönüş sağlanacaktır.
                        </p>
                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-medium px-7 py-6">
                                <MessageCircle className="h-5 w-5 mr-2" />
                                WhatsApp'tan Fotoğraf Gönder
                            </Button>
                        </a>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="lead-form"
            className="relative py-24 md:py-32 border-b border-white/5"
            data-testid="lead-form-section"
        >
            <div className="max-w-[1000px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5 md:sticky md:top-28">
                        <p className="text-xs uppercase tracking-[0.3em] text-cyan-300/80 mb-3">
                            Ön Değerlendirme Formu
                        </p>
                        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight text-balance mb-4">
                            Bilgilerinizi paylaşın, size dönelim
                        </h2>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                            Form üzerinden iletilen bilgiler kişisel ön değerlendirme için kullanılır.
                            Daha hızlı süreç için dövme/iz fotoğrafınızı WhatsApp üzerinden de
                            iletebilirsiniz.
                        </p>
                        <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
                            Sonuçlar kişiden kişiye değişebilir. Net seans sayısı ve uygunluk,
                            cilt yapısı ve dövme/iz analizi sonrası belirlenir.
                        </p>
                    </div>

                    <form
                        onSubmit={submit}
                        className="md:col-span-7 bg-[#0E0E0E] border border-white/5 rounded-2xl p-7 md:p-9 space-y-5"
                        data-testid="lead-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="full_name" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Ad Soyad *
                                </Label>
                                <Input
                                    id="full_name"
                                    value={data.full_name}
                                    onChange={(e) => update("full_name", e.target.value)}
                                    required
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-fullname"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Telefon *
                                </Label>
                                <Input
                                    id="phone"
                                    value={data.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    required
                                    placeholder="0 5xx xxx xx xx"
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-phone"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                                İşlem Türü
                            </Label>
                            <Select value={data.service_type} onValueChange={(v) => update("service_type", v)}>
                                <SelectTrigger
                                    className="bg-[#141414] border-white/10 text-white focus:ring-cyan-400/30"
                                    data-testid="form-service-type"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141414] border-white/10 text-white">
                                    {SERVICE_TYPE_OPTIONS.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="region" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Bölge / Şehir
                                </Label>
                                <Input
                                    id="region"
                                    value={data.region}
                                    onChange={(e) => update("region", e.target.value)}
                                    placeholder="İstanbul, Esenyurt..."
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-region"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="color" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Dövme / İz Rengi veya Görünümü
                                </Label>
                                <Input
                                    id="color"
                                    value={data.color_or_appearance}
                                    onChange={(e) => update("color_or_appearance", e.target.value)}
                                    placeholder="Siyah, renkli, kabarık iz..."
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-color"
                                />
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="size" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Boyut
                                </Label>
                                <Input
                                    id="size"
                                    value={data.size}
                                    onChange={(e) => update("size", e.target.value)}
                                    placeholder="Yaklaşık ölçü (cm)"
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-size"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="duration" className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Ne Kadar Süredir Var?
                                </Label>
                                <Input
                                    id="duration"
                                    value={data.duration}
                                    onChange={(e) => update("duration", e.target.value)}
                                    placeholder="Örn: 5 yıl"
                                    className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30"
                                    data-testid="form-duration"
                                />
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                                    Daha Önce İşlem Yapıldı mı?
                                </Label>
                                <Select
                                    value={data.previous_treatment}
                                    onValueChange={(v) => update("previous_treatment", v)}
                                >
                                    <SelectTrigger
                                        className="bg-[#141414] border-white/10 text-white"
                                        data-testid="form-previous"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#141414] border-white/10 text-white">
                                        <SelectItem value="Hayır">Hayır</SelectItem>
                                        <SelectItem value="Evet, lazer yapıldı">
                                            Evet, lazer yapıldı
                                        </SelectItem>
                                        <SelectItem value="Evet, başka yöntem">
                                            Evet, başka yöntem
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                                    İletişim Tercihi
                                </Label>
                                <Select
                                    value={data.contact_preference}
                                    onValueChange={(v) => update("contact_preference", v)}
                                >
                                    <SelectTrigger
                                        className="bg-[#141414] border-white/10 text-white"
                                        data-testid="form-contact-pref"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#141414] border-white/10 text-white">
                                        <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                                        <SelectItem value="Telefon">Telefon</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message" className="text-zinc-300 text-xs uppercase tracking-wider">
                                Mesajınız
                            </Label>
                            <Textarea
                                id="message"
                                value={data.message}
                                onChange={(e) => update("message", e.target.value)}
                                rows={4}
                                placeholder="Eklemek istediğiniz detaylar..."
                                className="bg-[#141414] border-white/10 text-white focus:border-cyan-400 focus:ring-cyan-400/30 resize-none"
                                data-testid="form-message"
                            />
                        </div>

                        <div className="rounded-xl bg-[#141414] border border-white/5 p-4 text-xs text-zinc-400 leading-relaxed">
                            <strong className="text-zinc-200 block mb-1">
                                Fotoğraf yükleme:
                            </strong>
                            Daha hızlı bir değerlendirme için dövme/iz fotoğrafınızı{" "}
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-300 hover:underline"
                            >
                                WhatsApp üzerinden
                            </a>{" "}
                            iletebilirsiniz.
                        </div>

                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="kvkk"
                                checked={data.kvkk_consent}
                                onCheckedChange={(v) => update("kvkk_consent", !!v)}
                                className="mt-1 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                                data-testid="form-kvkk"
                            />
                            <Label htmlFor="kvkk" className="text-xs text-zinc-400 leading-relaxed cursor-pointer">
                                Kişisel verilerimin{" "}
                                <a href="/kvkk-aydinlatma-metni" className="text-cyan-300 hover:underline">
                                    KVKK Aydınlatma Metni
                                </a>{" "}
                                kapsamında işlenmesini onaylıyorum. *
                            </Label>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-medium py-6 transition-all shadow-[0_0_20px_rgba(0,240,255,0.2)] hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] disabled:opacity-50"
                            data-testid="form-submit"
                        >
                            {submitting ? "Gönderiliyor..." : "Ön Değerlendirme İste"}
                        </Button>
                    </form>
                </div>
            </div>
        </section>
    );
}
